import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useParadigm } from '../../contexts/ParadigmContext';
import { useComponentEvents, useTodoEvents, useUrbanrideEvents, SSEEventType } from '../../contexts/SSEContext';
import { cancelTrip } from '../../services/urbanrideService';
import Timeline2 from './Timeline2';
import AddComponentModal from './AddComponentModal';
import EditComponentModal from './EditComponentModal';
import AssignExecutiveModal from './AssignExecutiveModal';
import ComponentDeleteModal from './ComponentDeleteModal';
import TripBookingModal from './TripBookingModal';
import ManageTripPermissionsModal from './ManageTripPermissionsModal';
import TodoModal from './TodoModal';
import CreateEmailAddressModal from './CreateEmailAddressModal';
import TripCompactHeader from './TripCompactHeader';
import EditTripModal2 from './EditTripModal2';
import { listEmailAddresses } from '../../services/emailAddressService';
import TodoSlidePanel from './TodoSlidePanel';

const TripDetails2 = ({ tripId: propTripId }) => {
  const { user } = useAuth();
  const { isSpeakerBureau } = useParadigm();
  const navigate = useNavigate();
  // Speaker portal accounts view trips strictly read-only (backend enforces
  // view_only too; this hides every mutating affordance in the UI)
  const readOnly = user?.role === 'speaker';
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modal states
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);
  const [addComponentPrefill, setAddComponentPrefill] = useState(null); // { type, componentData }
  const [editComponent, setEditComponent] = useState(null);
  const [showAssignExecutiveModal, setShowAssignExecutiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [componentToDelete, setComponentToDelete] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEditTripModal, setShowEditTripModal] = useState(false);

  // Todo states
  const [todos, setTodos] = useState([]);
  const [todoLoading, setTodoLoading] = useState(false);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [showTodoPanel, setShowTodoPanel] = useState(false);

  // Data states
  const [users, setUsers] = useState([]);
  const [allExecutives, setAllExecutives] = useState([]);
  const [tripExecutives, setTripExecutives] = useState([]);
  const [allTrips, setAllTrips] = useState([]);

  // Timeline control states - lifted from Timeline2 for compact header control
  const [viewMode, setViewMode] = useState(() => {
    // Try localStorage first for immediate access
    const localStorageKey = `timeline2_view_mode_${user?.id || 'default'}`;
    try {
      const savedMode = localStorage.getItem(localStorageKey);
      if (['summary', 'compact', 'cards', 'full'].includes(savedMode)) {
        return savedMode;
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    return user?.ui_preferences?.timeline2_view_mode || 'full';
  });
  const [urbanrideSelectionMode, setUrbanrideSelectionMode] = useState(false);

  // Persist view-mode changes the same way Timeline2's internal toggle does
  // (localStorage for fast reload + backend for cross-device). Previously,
  // changes made via the compact header were state-only and lost on reload.
  const handleViewModeChange = useCallback((newMode) => {
    setViewMode(newMode);
    try {
      localStorage.setItem(`timeline2_view_mode_${user?.id || 'default'}`, newMode);
    } catch (e) {
      // Ignore localStorage errors
    }
    axios.put('/users/me/preferences', { timeline2_view_mode: newMode }).catch((e) => {
      console.warn('Failed to save view mode preference to backend:', e);
    });
  }, [user?.id]);

  // State to trigger note creation in Timeline2
  const [triggerAddNote, setTriggerAddNote] = useState(0);

  // PDF Export state - lifted from Timeline2 for compact header control
  const [pdfExportMode, setPdfExportMode] = useState(false);
  const [pdfSelectedItems, setPdfSelectedItems] = useState(new Set());
  const [triggerPdfExport, setTriggerPdfExport] = useState(0);
  const [triggerPdfSelectAll, setTriggerPdfSelectAll] = useState(0);
  // Speaker bureaus default to (and only offer) the Speaker layout in place of
  // Executive; other formats are shared.
  const [pdfFormat, setPdfFormat] = useState(isSpeakerBureau ? 'speaker' : 'executive');

  // Keep the exclusive format valid if the paradigm resolves after mount
  // (Executive and Speaker are mutually exclusive per paradigm).
  useEffect(() => {
    setPdfFormat(prev => {
      if (isSpeakerBureau && prev === 'executive') return 'speaker';
      if (!isSpeakerBureau && prev === 'speaker') return 'executive';
      return prev;
    });
  }, [isSpeakerBureau]);

  // Email lock state
  const [eaEmailsLocked, setEaEmailsLocked] = useState(false);
  const [activePackageName, setActivePackageName] = useState('');

  const tripId = useMemo(() => {
    if (propTripId) return propTripId;
    try { return new URLSearchParams(window.location.search).get('id'); } catch { return null; }
  }, [propTripId]);

  const apiBase = getApiBase();
  // Memoize headers to prevent infinite re-renders in useCallbacks that depend on it
  const headers = useMemo(() => getAuthHeaders(), []);

  // Refresh trip data
  const refreshTrip = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    setIsRefreshing(true);
    setError('');
    try {
      const res = await axios.get(`${apiBase}/trips/${tripId}`, { headers });
      setTrip(res.data || null);
    } catch (e) {
      // A 403/404 means the trip isn't in the current organization (e.g. a
      // stale/bookmarked URL, or the org was switched) or was deleted - not a
      // real load failure. Surface that distinctly so it reads as "not here"
      // rather than a scary error.
      const status = e?.response?.status;
      if (status === 403 || status === 404) {
        setError('not_in_org');
      } else {
        setError('Failed to load trip details');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [tripId, apiBase, headers]);

  // Load todos
  const loadTodos = useCallback(async () => {
    if (!tripId) return;
    setTodoLoading(true);
    try {
      const res = await axios.get(`${apiBase}/todos`, { params: { trip_id: tripId }, headers });
      setTodos(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setTodos([]);
    } finally {
      setTodoLoading(false);
    }
  }, [tripId, apiBase, headers]);

  // Load users
  const loadUsers = async () => {
    try {
      const res = await axios.get(`${apiBase}/users`, { headers });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load users', e);
    }
  };

  // Load all trips
  const loadAllTrips = async () => {
    try {
      const res = await axios.get(`${apiBase}/trips`, { params: { limit: 100 }, headers });
      const tripsData = res.data?.trips || res.data || [];
      setAllTrips(Array.isArray(tripsData) ? tripsData : []);
    } catch (e) {
      console.error('Failed to load all trips', e);
    }
  };

  // Load executives
  const loadExecutives = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/executives`, { headers });
      const executives = Array.isArray(res.data) ? res.data : [];
      setAllExecutives(executives);

      if (trip && trip.executive_ids) {
        const assigned = executives.filter(exec => trip.executive_ids.includes(exec.id));
        setTripExecutives(assigned);
      } else if (trip && trip.executive_names) {
        const assigned = executives.filter(exec => {
          const fullName = exec.full_name || `${exec.first_name} ${exec.last_name}`;
          return trip.executive_names.includes(fullName);
        });
        setTripExecutives(assigned);
      } else {
        setTripExecutives([]);
      }
    } catch (e) {
      console.error('Failed to load executives:', e);
      setAllExecutives([]);
      setTripExecutives([]);
    }
  }, [apiBase, headers, trip]);

  // Initial data loading
  useEffect(() => { refreshTrip(); }, [tripId]);
  useEffect(() => { loadAllTrips(); }, []);
  useEffect(() => {
    loadTodos();
    loadUsers();
  }, [tripId, loadTodos]);
  useEffect(() => { if (trip) loadExecutives(); }, [trip, loadExecutives]);

  // Fetch email lock status
  useEffect(() => {
    const fetchEmailLockStatus = async () => {
      try {
        const result = await listEmailAddresses();
        setEaEmailsLocked(result.ea_emails_locked_by_package || false);
        setActivePackageName(result.active_package_name || '');
      } catch (err) {
        console.error('Error fetching email lock status:', err);
      }
    };
    fetchEmailLockStatus();
  }, []);

  // SSE: Handle component events
  const handleComponentEvent = useCallback((event) => {
    const eventTripId = event.trip_id;
    if (!tripId || eventTripId !== tripId) return;
    refreshTrip();
  }, [tripId, refreshTrip]);

  useComponentEvents(handleComponentEvent, [tripId, refreshTrip]);

  // SSE: Handle Urbanride webhook events (chauffeur assigned, vehicle status, etc.)
  const handleUrbanrideEvent = useCallback((event) => {
    const eventTripId = event.data?.trip_id || event.trip_id;
    if (!tripId || eventTripId !== tripId) return;
    refreshTrip();
  }, [tripId, refreshTrip]);

  useUrbanrideEvents(handleUrbanrideEvent, [tripId, refreshTrip]);

  // SSE: Handle todo events
  const handleTodoEvent = useCallback((event) => {
    const todoData = event.data?.todo;
    if (todoData?.trip_id !== tripId) return;

    if (event.type === SSEEventType.TODO_CREATED && todoData) {
      setTodos((prev) => {
        if (prev.some((t) => t.id === todoData.id)) return prev;
        return [...prev, todoData];
      });
    } else if (event.type === SSEEventType.TODO_UPDATED && todoData) {
      setTodos((prev) =>
        prev.map((t) => (t.id === todoData.id ? { ...t, ...todoData } : t))
      );
    } else if (event.type === SSEEventType.TODO_DELETED && todoData) {
      setTodos((prev) => prev.filter((t) => t.id !== todoData.id));
    }
  }, [tripId]);

  useTodoEvents(handleTodoEvent, [tripId]);

  // Handle editComponent query parameter
  useEffect(() => {
    if (!trip || !trip.components) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const componentIdToEdit = params.get('editComponent');
      if (componentIdToEdit) {
        const component = trip.components.find(c => c.id === componentIdToEdit);
        if (component) {
          setEditComponent(component);
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      }
    } catch (error) {
      console.error('Error parsing editComponent query parameter:', error);
    }
  }, [trip]);

  // Component handlers
  const handleEditComponent = (component) => setEditComponent(component);

  // Open the Add Component flow pre-filled for an Airport VIP greet on a specific flight leg
  const handleAddVipServiceForLeg = (componentId, legIndex, direction) => {
    if (!componentId || (direction !== 'arrival' && direction !== 'departure')) return;
    setAddComponentPrefill({
      type: 'airport_vip_service',
      componentData: {
        linked_segment_id: `${componentId}::leg::${legIndex || 0}`,
        service_direction: direction,
      },
    });
    setShowAddComponentModal(true);
  };

  const handleDeleteComponent = async (componentOrId) => {
    const componentId = typeof componentOrId === 'string' ? componentOrId : componentOrId?.id;
    if (!tripId || !componentId) return;
    const component = components.find(c => c.id === componentId);
    if (!component) {
      alert('Component not found');
      return;
    }
    setComponentToDelete(component);
    setShowDeleteModal(true);
  };

  const handleReassignComponent = async (component, targetTripId) => {
    if (!tripId || !component?.id || !targetTripId) return;
    try {
      const formData = new FormData();
      formData.append('target_trip_id', targetTripId);
      const reassignHeaders = { ...headers };
      delete reassignHeaders['Content-Type'];
      await axios.post(
        `${apiBase}/trips/${tripId}/components/${component.id}/reassign`,
        formData,
        { headers: reassignHeaders }
      );
      refreshTrip();
      loadAllTrips();
    } catch (err) {
      console.error('Error reassigning component:', err);
      alert('Failed to move component to another trip');
    }
  };

  const handleInlineUpdateComponent = async (component, updatedData) => {
    if (!tripId || !component?.id) return;
    try {
      await axios.put(`${apiBase}/trips/${tripId}/components/${component.id}`, updatedData, { headers });
      await refreshTrip();
    } catch (err) {
      console.error('Error inline-updating component:', err);
    }
  };

  // Helper to clean up urbanride references from linked components
  const cleanupUrbanrideReferences = async (linkedComponentIds, urcnToRemove) => {
    const currentComponents = Array.isArray(trip?.components) ? trip.components : [];
    for (const linkedId of linkedComponentIds) {
      const linkedComponent = currentComponents.find(c => c.id === linkedId);
      if (!linkedComponent) continue;
      const componentData = linkedComponent.component_data || {};

      // Check if this component has any urbanride references to clean
      const currentBookings = componentData.urbanride_bookings || [];
      const legacyUrcn = componentData.urbanride_booking_urcn;

      // Skip if no urbanride references at all
      if (currentBookings.length === 0 && !legacyUrcn) continue;

      // Build updated component_data
      const updatedComponentData = { ...componentData };
      let needsUpdate = false;

      // Clean urbanride_bookings array (new format)
      if (currentBookings.length > 0) {
        const updatedBookings = currentBookings.filter(b => b.urcn !== urcnToRemove);
        if (updatedBookings.length !== currentBookings.length) {
          updatedComponentData.urbanride_bookings = updatedBookings;
          needsUpdate = true;
        }
      }

      // Clean legacy fields if they match the URCN being removed
      if (legacyUrcn === urcnToRemove) {
        delete updatedComponentData.urbanride_booking_urcn;
        delete updatedComponentData.urbanride_booking_leg_indices;
        needsUpdate = true;
      }

      if (!needsUpdate) continue;

      try {
        await axios.put(
          `${apiBase}/trips/${tripId}/components/${linkedId}`,
          { component_data: updatedComponentData },
          { headers }
        );
      } catch (cleanupError) {
        console.warn(`Failed to cleanup urbanride reference for component ${linkedId}:`, cleanupError);
      }
    }
  };

  const confirmDeleteComponent = async () => {
    if (!componentToDelete) return;
    try {
      const isChauffeuredService = componentToDelete.component_type === 'chauffeur_service';
      const componentData = componentToDelete.component_data || {};
      const urcn = componentToDelete.confirmation_number || componentData.urbanride_urcn || componentData.confirmation_number;

      if (isChauffeuredService && urcn) {
        try {
          await cancelTrip(urcn);
        } catch (cancelError) {
          console.warn(`Failed to cancel Urbanride booking ${urcn}:`, cancelError.message || cancelError);
        }
        const linkedComponentIds = componentData.linked_component_ids || [];
        if (linkedComponentIds.length > 0) {
          await cleanupUrbanrideReferences(linkedComponentIds, urcn);
        }
      }

      await axios.delete(`${apiBase}/trips/${tripId}/components/${componentToDelete.id}`, { headers });
      await refreshTrip();
      setShowDeleteModal(false);
      setComponentToDelete(null);
    } catch (e) {
      alert('Failed to delete component: ' + (e.response?.data?.detail || e.message));
      setShowDeleteModal(false);
      setComponentToDelete(null);
    }
  };

  const cancelDeleteComponent = () => {
    setShowDeleteModal(false);
    setComponentToDelete(null);
  };

  // Todo handlers
  const handleCreateTodo = async (todoData) => {
    try {
      await axios.post(`${apiBase}/todos`, { ...todoData, trip_id: tripId }, { headers });
      loadTodos();
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteTodo = async (todo) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) return;
    try {
      await axios.delete(`${apiBase}/todos/${todo.id}`, { headers });
      loadTodos();
    } catch (err) {
      alert('Failed to delete todo: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateTodo = async (todoId, updateData) => {
    try {
      await axios.put(`${apiBase}/todos/${todoId}`, updateData, { headers });
      loadTodos();
    } catch (err) {
      alert('Failed to update todo: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleTodoStatus = (todo) => {
    const newStatus = todo.status === 'completed' ? 'open' : 'completed';
    handleUpdateTodo(todo.id, { status: newStatus });
  };

  const handleTodoModalClose = () => {
    setShowTodoModal(false);
    setEditingTodo(null);
  };

  const handleTodoModalSuccess = () => {
    loadTodos();
    handleTodoModalClose();
  };

  const handleEditTodo = (todo) => {
    setEditingTodo(todo);
    setShowTodoModal(true);
  };

  // Calculate pending todos
  const pendingTodos = useMemo(() =>
    todos.filter(t => t.status !== 'completed'),
    [todos]
  );

  const hasOverdueTodos = useMemo(() =>
    pendingTodos.some(t => t.due_date && new Date(t.due_date) < new Date()),
    [pendingTodos]
  );

  // Loading and error states
  if (!tripId) return <div className="p-4 text-ui-red-fg bg-ui-page">No trip selected.</div>;
  if (loading && !trip) return <div className="p-4 text-ui-ink-2 bg-ui-page">Loading trip...</div>;
  if (error === 'not_in_org') {
    return (
      <div className="flex items-center justify-center p-6 min-h-screen bg-ui-page">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">🧭</div>
          <h2 className="text-xl font-semibold mb-2 text-ui-ink">This trip isn't in the current organization</h2>
          <p className="mb-6 text-ui-ink-3">
            It may belong to a different organization, or it may have been deleted.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 rounded-xl font-semibold bg-ui-primary text-ui-on-primary hover:bg-ui-primary-hover transition-colors"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }
  if (error) return <div className="p-4 text-ui-red-fg bg-ui-page">{error}</div>;
  if (!trip) return <div className="p-4 text-ui-ink-2 bg-ui-page">Trip not found.</div>;

  const components = Array.isArray(trip.components) ? trip.components : [];

  return (
    <div className="min-h-screen bg-ui-page">
      {/* Compact Header */}
      <TripCompactHeader
        trip={trip}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onAddNote={() => setTriggerAddNote(prev => prev + 1)}
        onUrbanrideClick={() => setUrbanrideSelectionMode(!urbanrideSelectionMode)}
        urbanrideSelectionMode={urbanrideSelectionMode}
        onAddComponent={() => setShowAddComponentModal(true)}
        onEditTrip={() => setShowEditTripModal(true)}
        onManagePermissions={() => setShowPermissionsModal(true)}
        onManageExecutives={() => setShowAssignExecutiveModal(true)}
        onManageEmail={() => setShowEmailModal(true)}
        eaEmailsLocked={eaEmailsLocked}
        onRefresh={refreshTrip}
        isRefreshing={isRefreshing}
        pendingTodosCount={pendingTodos.length}
        hasOverdueTodos={hasOverdueTodos}
        onTodosClick={() => setShowTodoPanel(true)}
        // PDF Export props
        pdfExportMode={pdfExportMode}
        onPdfExportClick={() => {
          setPdfExportMode(true);
          // Trigger select all when entering PDF mode
          setTriggerPdfSelectAll(prev => prev + 1);
        }}
        onPdfExportCancel={() => {
          setPdfExportMode(false);
          setPdfSelectedItems(new Set());
        }}
        onPdfExportGenerate={() => setTriggerPdfExport(prev => prev + 1)}
        onPdfSelectAll={() => setTriggerPdfSelectAll(prev => prev + 1)}
        pdfSelectedCount={pdfSelectedItems.size}
        pdfTotalCount={components.length}
        pdfFormat={pdfFormat}
        onPdfFormatChange={setPdfFormat}
        isSpeakerBureau={isSpeakerBureau}
        readOnly={readOnly}
      />

      {/* Main Content - Timeline */}
      <div className="px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
        <Timeline2
          trip={trip}
          components={components}
          executives={tripExecutives}
          allExecutives={allExecutives}
          onTripUpdate={setTrip}
          onEditComponent={readOnly ? undefined : handleEditComponent}
          onDeleteComponent={readOnly ? undefined : handleDeleteComponent}
          onReassignComponent={readOnly ? undefined : handleReassignComponent}
          onInlineUpdateComponent={readOnly ? undefined : handleInlineUpdateComponent}
          onAddVipServiceForLeg={readOnly ? undefined : handleAddVipServiceForLeg}
          readOnly={readOnly}
          allTrips={allTrips}
          onRefreshComponents={refreshTrip}
          // Compact header mode props
          hideHeader={true}
          externalUrbanrideMode={urbanrideSelectionMode}
          onUrbanrideModeChange={setUrbanrideSelectionMode}
          externalViewMode={viewMode}
          onExternalViewModeChange={handleViewModeChange}
          triggerAddNote={triggerAddNote}
          // PDF Export props
          externalPdfExportMode={pdfExportMode}
          onPdfExportModeChange={setPdfExportMode}
          externalPdfSelectedItems={pdfSelectedItems}
          onPdfSelectedItemsChange={setPdfSelectedItems}
          triggerPdfExport={triggerPdfExport}
          triggerPdfSelectAll={triggerPdfSelectAll}
          pdfFormat={pdfFormat}
        />
      </div>

      {/* Todo Slide Panel */}
      <TodoSlidePanel
        isOpen={showTodoPanel}
        onClose={() => setShowTodoPanel(false)}
        todos={todos}
        isLoading={todoLoading}
        onToggleTodo={handleToggleTodoStatus}
        onCreateTodo={handleCreateTodo}
        onEditTodo={handleEditTodo}
        onDeleteTodo={handleDeleteTodo}
      />

      {/* Modals */}
      {showAddComponentModal && (
        <AddComponentModal
          tripId={tripId}
          onClose={() => { setShowAddComponentModal(false); setAddComponentPrefill(null); }}
          onSuccess={async () => { setShowAddComponentModal(false); setAddComponentPrefill(null); await refreshTrip(); }}
          tripExecutives={tripExecutives}
          allExecutives={allExecutives}
          meetingExecutives={tripExecutives}
          allContacts={[]}
          tripComponents={trip?.components || []}
          initialType={addComponentPrefill?.type || null}
          initialComponentData={addComponentPrefill?.componentData || null}
        />
      )}

      {editComponent && (
        <EditComponentModal
          component={editComponent}
          onClose={() => setEditComponent(null)}
          onSuccess={async () => { setEditComponent(null); await refreshTrip(); }}
          tripId={tripId}
          tripExecutives={tripExecutives}
          allExecutives={allExecutives}
          allContacts={[]}
          tripComponents={trip?.components || []}
        />
      )}

      {showAssignExecutiveModal && (
        <AssignExecutiveModal
          trip={trip}
          onClose={() => setShowAssignExecutiveModal(false)}
          onUpdate={refreshTrip}
        />
      )}

      {showDeleteModal && componentToDelete && (
        <ComponentDeleteModal
          component={componentToDelete}
          trip={trip}
          onConfirm={confirmDeleteComponent}
          onCancel={cancelDeleteComponent}
        />
      )}

      {showBookingModal && (
        <TripBookingModal
          tripId={tripId}
          trip={trip}
          onClose={() => setShowBookingModal(false)}
          onSuccess={refreshTrip}
        />
      )}

      {showPermissionsModal && (
        <ManageTripPermissionsModal
          trip={trip}
          currentUser={user}
          onClose={() => setShowPermissionsModal(false)}
          onUpdate={refreshTrip}
        />
      )}

      {showTodoModal && (
        <TodoModal
          todo={editingTodo}
          users={users}
          defaultTripId={tripId}
          onClose={handleTodoModalClose}
          onSuccess={handleTodoModalSuccess}
        />
      )}

      {showEmailModal && trip && (
        <CreateEmailAddressModal
          open={showEmailModal}
          onOpenChange={setShowEmailModal}
          type="trip"
          tripId={tripId}
          tripTitle={trip.title}
          onSuccess={() => refreshTrip()}
        />
      )}

      {showEditTripModal && trip && (
        <EditTripModal2
          trip={trip}
          executives={allExecutives}
          onClose={() => setShowEditTripModal(false)}
          onTripUpdated={(updatedTrip) => {
            setTrip(updatedTrip);
            setShowEditTripModal(false);
          }}
        />
      )}
    </div>
  );
};

export default TripDetails2;
